import React from 'react';
import { Autocomplete, Grid, TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

export const DATE_FILTER_PRESETS = [
  { id: 'all', name: 'All' },
  { id: 'today', name: 'Today' },
  { id: 'yesterday', name: 'Yesterday' },
  { id: 'this_month', name: 'This Month' },
  { id: 'custom', name: 'Date Range' },
];

export const EMPTY_DATE_FILTER = { preset: 'all', from: null, to: null };

/**
 * turn a preset into a concrete from/to pair
 * @author Akhil E M
 * @createdAt 04-08-2026
 * @updates 04-08-2026(Akhil EM),
 * @type function
 * @packages dayjs
 * @param {string} preset
 * @returns {{from: object|null, to: object|null}}
 */
export const resolvePresetRange = (preset) => {
  const today = dayjs().startOf('day');
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const yesterday = today.subtract(1, 'day');
      return { from: yesterday, to: yesterday };
    }
    case 'this_month':
      //WHOLE CALENDAR MONTH , NOT MONTH TO DATE
      return { from: today.startOf('month'), to: today.endOf('month').startOf('day') };
    default:
      return { from: null, to: null };
  }
};

//THE API EXPECTS YYYY-MM-DD OR NOTHING AT ALL
export const toApiDate = (value) => (value ? dayjs(value).format('YYYY-MM-DD') : null);

const GRID_SIZE = { xs: 12, sm: 6, md: 4, lg: 3, xl: 2.4 };

/**
 * preset driven date range filter ( Today / Yesterday / This Month / Date Range ).
 * renders Grid items , so it has to be placed inside a Grid container.
 * @author Akhil E M
 * @createdAt 04-08-2026
 * @updates 04-08-2026(Akhil EM),
 * @type component
 * @packages @mui/x-date-pickers,dayjs
 */
const DateRangeFilter = ({ label, value = EMPTY_DATE_FILTER, onChange, disableFuture = false }) => {
  const preset = value?.preset ?? 'all';
  const isCustom = preset === 'custom';

  const handlePresetChange = (event, option) => {
    const nextPreset = option?.id ?? 'all';
    //SWITCHING TO "DATE RANGE" KEEPS WHATEVER DATES ARE ALREADY THERE INSTEAD OF WIPING THEM
    if (nextPreset === 'custom') {
      onChange({ preset: nextPreset, from: value?.from ?? null, to: value?.to ?? null });
      return;
    }
    onChange({ preset: nextPreset, ...resolvePresetRange(nextPreset) });
  };

  const handleFromChange = (nextFrom) => {
    //KEEP THE RANGE COHERENT WHEN THE NEW START OVERTAKES THE EXISTING END
    const resetTo = nextFrom && value?.to && dayjs(nextFrom).isAfter(dayjs(value.to), 'day');
    onChange({ preset: 'custom', from: nextFrom, to: resetTo ? null : (value?.to ?? null) });
  };

  const handleToChange = (nextTo) => {
    onChange({ preset: 'custom', from: value?.from ?? null, to: nextTo });
  };

  const datePickerSlotProps = {
    textField: {
      fullWidth: true,
      autoComplete: 'off',
      InputProps: { readOnly: true }, //PICK FROM THE CALENDAR , NO FREE TYPING
    },
  };

  return (
    <>
      <Grid item {...GRID_SIZE}>
        <Autocomplete
          fullWidth
          disableClearable
          options={DATE_FILTER_PRESETS}
          getOptionLabel={(option) => option.name || ''}
          isOptionEqualToValue={(option, selected) => option.id === selected.id}
          value={DATE_FILTER_PRESETS.find((option) => option.id === preset) || DATE_FILTER_PRESETS[0]}
          onChange={handlePresetChange}
          renderInput={(params) => <TextField fullWidth autoComplete="off" {...params} label={label} />}
        />
      </Grid>

      {isCustom && (
        <Grid item {...GRID_SIZE}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={`${label} From`}
              value={value?.from ?? null}
              onChange={handleFromChange}
              maxDate={value?.to || undefined}
              disableFuture={disableFuture}
              format="DD-MM-YYYY"
              views={['year', 'month', 'day']}
              slotProps={datePickerSlotProps}
            />
          </LocalizationProvider>
        </Grid>
      )}

      {isCustom && (
        <Grid item {...GRID_SIZE}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={`${label} To`}
              value={value?.to ?? null}
              onChange={handleToChange}
              minDate={value?.from || undefined}
              disableFuture={disableFuture}
              format="DD-MM-YYYY"
              views={['year', 'month', 'day']}
              slotProps={datePickerSlotProps}
            />
          </LocalizationProvider>
        </Grid>
      )}
    </>
  );
};

export default DateRangeFilter;
